import { logger } from '@automata/utils';
import sortBy from 'lodash/sortBy';
import { Edge } from 'reactflow';

import { selectLabwareDisplayDataByTaskId } from '../../features/selectors';
import { AppThunk, RootState } from '../../store';
import {
  type CreateLabwareData,
  type CreateTaskData,
  type GraphEdges,
  NodeType,
  type TimeConstraintInstance,
} from '../../types';
import { subtractPositions } from '../../utils';
import { copyNodeToClipboard } from '../copyPaste/copyPasteSlice';
import {
  selectAllNodePositions,
  selectDataDependencyEdges,
  selectHandlePairIdOrderByNode,
  selectLabwareDependencyEdges,
  selectLabwareEdgesWithDuplicatedTarget,
  selectNodeIdToColorIndex,
  selectSelectedNodeIds,
  selectSelectedNodePosition,
  selectSelectedNodes,
  selectTimeConstraintInstances,
} from '../graph/selectors';
import { selectFlowPositionCenter } from '../ui/selectors';
import {
  selectCommentsById,
  selectLabwareProperties,
  selectTaskPropertiesStrict,
} from '../workflow/selectors';

import { cutNodeToClipboard } from '.';

export const getLabwareId = (
  state: RootState,
  handlePairId?: string | null,
  handle?: string | null
) => {
  if (!handlePairId || !handle) return;

  const handleId = handle.split('.')[1];

  if (!handleId) return;

  // if handlePairId is a labware node id, return it directly
  if (handlePairId.startsWith('labware___')) return handleId;
  // otherwise, look up labware id by task id and handlePairId
  return selectLabwareDisplayDataByTaskId(state)[handlePairId]?.[handleId]?.id;
};

export const getSameOriginEdge = (
  edges: Edge[],
  source: string,
  sourceHandle?: string | null
) => {
  return edges.find(
    (e) => e.source === source && e.sourceHandle === sourceHandle
  );
};

export const getSameTargetsWithDifferentLabware = (
  edges: Edge[],
  target: string,
  targetHandle: string | null,
  startLabwareId: string | undefined,
  state: RootState
): Edge[] => {
  return edges.filter((e) => {
    if (e.target !== target || e.targetHandle !== targetHandle) return false;

    const edgeLabwareId = getLabwareId(state, e.source, e.sourceHandle);
    return edgeLabwareId !== startLabwareId;
  });
};

export const getDeleteConflictEdgeIds = (
  newState: RootState,
  startLabwareId: string
): string[] => {
  const duplicatedEdgesByTarget =
    selectLabwareEdgesWithDuplicatedTarget(newState);
  const deleteConflictEdgeIds: string[] = [];

  for (const group of Object.values(duplicatedEdgesByTarget)) {
    const labwareIds = group
      .map((edge) => getLabwareId(newState, edge.source, edge.sourceHandle))
      .filter((v): v is string => v !== undefined);

    const uniqueLabwareIds = new Set(labwareIds);
    if (uniqueLabwareIds.size <= 1) continue;

    for (const edge of group) {
      const id = getLabwareId(newState, edge.source, edge.sourceHandle);
      if (id !== startLabwareId) deleteConflictEdgeIds.push(edge.id);
    }
  }

  return deleteConflictEdgeIds;
};
// TODO(IL, 2024-04-19): DRY this code.
export const handleCutCopyData = (
  dispatch: Parameters<AppThunk>[0],
  getState: Parameters<AppThunk>[1],
  mode: 'cut' | 'copy'
): void => {
  const state = getState();
  const selectedNodes = selectSelectedNodes(state);
  const selectedNodePosition = selectSelectedNodePosition(state);
  const allNodePositions = selectAllNodePositions(state);
  const commentsById = selectCommentsById(state);
  const labwareDependencyEdges = selectLabwareDependencyEdges(state);
  const handlePairIdOrderByNode = selectHandlePairIdOrderByNode(state);
  const dataDependencyEdges = selectDataDependencyEdges(state);

  const action = mode === 'cut' ? cutNodeToClipboard : copyNodeToClipboard;

  if (selectedNodes.length === 0) {
    return;
  }

  if (selectedNodePosition === null) {
    logger.error('Expected non-null selectedNodePosition during copy command');
  }

  if (selectedNodes.length === 1) {
    // Single-item copy

    const firstNode = selectedNodes[0];
    if (firstNode === undefined) {
      logger.error('selected node is undefined');
      return;
    }
    const { id, nodeType } = firstNode;
    const position = allNodePositions[id];
    const handlePairIdOrder = handlePairIdOrderByNode[id] ?? [];

    const selectedLabwareDependencyEdges: GraphEdges =
      labwareDependencyEdges.filter(
        (edge) => edge.source === id || edge.target === id
      );

    const selectedDataDependencyEdges: GraphEdges = dataDependencyEdges.filter(
      (edge) => edge.source === id || edge.target === id
    );

    if (nodeType === NodeType.TASK) {
      const taskProperties = selectTaskPropertiesStrict(state)[id];

      if (taskProperties === undefined || position === undefined) {
        logger.error(`could not copy task ${id}`, {
          taskProperties,
          position,
        });
        return;
      }
      dispatch(
        action({
          dataType: 'task',
          data: {
            properties: taskProperties,
            position,
            handlePairIdOrder,
            comments: commentsById[id],
          },
          edges: selectedLabwareDependencyEdges,
          dataDependencyEdges: selectedDataDependencyEdges,
        })
      );
    } else if (nodeType === NodeType.LABWARE) {
      const labwareProperties = selectLabwareProperties(state)[id];
      const colorIndex = selectNodeIdToColorIndex(state)[id];

      if (labwareProperties === undefined || position === undefined) {
        logger.error(`could not copy labware ${id}`, {
          labwareProperties,
          position,
        });
        return;
      }
      dispatch(
        action({
          dataType: 'labware',
          data: {
            properties: labwareProperties,
            position,
            colorIndex,
            comments: commentsById[id],
          },
          edges: selectedLabwareDependencyEdges,
        })
      );
    } else if (nodeType === NodeType.CONDITIONAL_TASK) {
      // TODO: Implement ConditionalTask copy
    } else {
      nodeType satisfies never;
    }
  } else {
    // multi-item copy
    const allNodePositions = selectAllNodePositions(state);
    const allTimeConstraints = selectTimeConstraintInstances(state);
    const labwarePropertiesById = selectLabwareProperties(state);
    const taskPropertiesById = selectTaskPropertiesStrict(state);
    const selectedNodeIds = selectSelectedNodeIds(state);
    const flowPositionCenter = selectFlowPositionCenter(state);
    const nodeIdToColorIndex = selectNodeIdToColorIndex(state);

    const hasSelectedSourceAndTargetNode = ({
      source,
      target,
    }: {
      source: string;
      target: string;
    }): boolean =>
      selectedNodeIds.includes(source) && selectedNodeIds.includes(target);

    // NOTE(IL, 2024-04-19): it doesn't make sense to copy edges not immediately connected to a selected node,
    // nor does it make sense to omit edges between selected nodes. So we intentionally ignore the user's
    // edge selection, and copy every edge that exists between the selected nodes. This is true both of
    // labware dependencies and of time constraints.

    const selectedLabwareDependencyEdges: GraphEdges =
      labwareDependencyEdges.filter(hasSelectedSourceAndTargetNode);

    const selectedDataDependencyEdges: GraphEdges = dataDependencyEdges.filter(
      hasSelectedSourceAndTargetNode
    );

    const timeConstraints: TimeConstraintInstance[] = Object.values(
      allTimeConstraints
    ).filter(hasSelectedSourceAndTargetNode);

    const middlePosition = selectedNodePosition ?? flowPositionCenter;

    const labwareNodes: CreateLabwareData[] = [];
    const taskNodes: CreateTaskData[] = [];

    for (const node of selectedNodes) {
      const { nodeType, id } = node;
      const comments = commentsById[id];

      const absolutePosition = allNodePositions[id];
      if (absolutePosition === undefined) {
        logger.error(
          `missing position for node ${id} while copying node, using (0, 0) for relative pos`
        );
      }
      const relativePosition =
        absolutePosition === undefined
          ? { x: 0, y: 0 }
          : subtractPositions(absolutePosition, middlePosition);

      if (nodeType === NodeType.LABWARE) {
        const properties = labwarePropertiesById[id];
        const colorIndex = nodeIdToColorIndex[id];

        if (properties === undefined) {
          logger.error(`could not copy ${nodeType} ${id}, missing data`, {
            properties,
          });
        } else {
          labwareNodes.push({
            properties,
            position: relativePosition,
            colorIndex,
            comments,
          });
        }
      } else if (nodeType === NodeType.TASK) {
        const properties = taskPropertiesById[id];
        const handlePairIdOrder = handlePairIdOrderByNode[id] ?? [];
        if (properties === undefined) {
          logger.error(`could not copy ${nodeType} ${id}, missing data`, {
            properties,
          });
        } else {
          taskNodes.push({
            properties,
            handlePairIdOrder,
            position: relativePosition,
            comments,
          });
        }
      } else if (nodeType === NodeType.CONDITIONAL_TASK) {
        // TODO: Implement ConditionalTask copy
      } else {
        nodeType satisfies never;
      }
    }

    // NOTE(IL, 2024-08-09): these arrays are sorted so they're deterministic, eg for tests
    dispatch(
      action({
        dataType: 'multi',
        edges: sortBy(selectedLabwareDependencyEdges, 'id'),
        labwareNodes: sortBy(labwareNodes, 'properties.id'),
        taskNodes: sortBy(taskNodes, 'properties.id'),
        timeConstraints: sortBy(timeConstraints, 'id'),
        dataDependencyEdges: sortBy(selectedDataDependencyEdges, 'id'),
      })
    );
  }
};
